/* RecorderSession — fullscreen overlay that polls the recorder VMSS and
   renders the live cloud-browser walk. Shared by all recorder surfaces
   (public Site Check funnel, Studio recordings, the account app) — one
   component, one design.

   State machine: queued → starting → recording → done → finalise.
   Contexta-branded recorder window: header (heading + RECORDING status pill +
   Finish-recording button), a contained browser card (chrome + live view),
   and a stats rail. Warm-up / saving / analysing share a brand status card. */

import { useEffect, useRef, useState, useCallback } from 'react';

const POLL_MS = 1000;

function useRecorderClientScript() {
  useEffect(() => {
    if (document.querySelector('script[src*="recorder-client.js"]')) return;
    const s = document.createElement('script');
    s.src = '/recorder/cloud-record/static/recorder-client.js';
    document.head.appendChild(s);
    return () => { try { document.head.removeChild(s); } catch (_) {} };
  }, []);
}

function useCdpClientScript() {
  useEffect(() => {
    if (document.querySelector('script[src*="cdp-client.js"]')) return;
    const s = document.createElement('script');
    s.src = '/recorder/cloud-record/static/cdp-client.js';
    document.head.appendChild(s);
    return () => { try { document.head.removeChild(s); } catch (_) {} };
  }, []);
}
const STUCK_AFTER_MS = 30000;

export default function RecorderSession({ session, onClose, onDone, heading = 'Walk Your Site' }) {
  const [phase, setPhase] = useState('starting');
  const [error, setError] = useState(null);
  const [vncPath, setVncPath] = useState(null);
  const [cdpWsUrl, setCdpWsUrl] = useState(null);  // cdp-screencast transport
  const [reqCount, setReqCount] = useState(0);
  const [stats, setStats] = useState(null);   // live walk totals (backend: capture_stats)
  const [lastUrl, setLastUrl] = useState('');
  const [countdown, setCountdown] = useState('10:00');
  const [stuck, setStuck] = useState(false);
  const [authOverlay, setAuthOverlay] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [walkSummary, setWalkSummary] = useState(null);  // post-walk reveal counts
  const [queuePos, setQueuePos] = useState(session.queue_position || null);

  const pollRef = useRef(null);
  const startedRef = useRef(null);
  const capRef = useRef(600);
  const lastCountRef = useRef(-1);
  const lastChangeRef = useRef(Date.now());
  const authShownRef = useRef(false);
  const iframeRef = useRef(null);
  const canvasRef = useRef(null);
  const cdpHandleRef = useRef(null);
  const countdownRef = useRef(null);

  const stopPoll = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const stopCountdown = useCallback(() => {
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
  }, []);

  // -- Finalise: POST /finish, then poll analyse job --
  const finalise = useCallback(async () => {
    setSaving(true);
    setPhase('saving');
    setSaveMsg('Saving recording...');
    stopPoll();
    stopCountdown();
    try {
      const r = await fetch(session.finish_url, { method: 'POST', credentials: 'same-origin' });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        setError(e.error || `finish failed (${r.status})`);
        setSaving(false);
        return;
      }
      const data = await r.json();
      if (data.walk_summary) setWalkSummary(data.walk_summary);
      if (data.redirect_url) { window.location.href = data.redirect_url; return; }

      // Studio context: logged-in user gets report_url_template pointing
      // at /admin/queue/recordings/<id>. The analyse job runs in the
      // background (Site Check L1 report) but the Studio user doesn't
      // need to wait for it — they want the recording, not the report.
      // Close the overlay and refresh the recordings list.
      if (data.recording_id && (!data.poll_url || (data.report_url_template || '').startsWith('/admin/'))) {
        setSaveMsg('Recording saved (ID ' + data.recording_id + '). Returning to recordings…');
        setTimeout(onClose, 1500);
        return;
      }

      if (!data.poll_url) {
        setSaveMsg(data.message || 'Recording saved.');
        setTimeout(onClose, 2000);
        return;
      }

      setSaveMsg(data.message || 'Reading the captured pages, scoring security and performance…');
      setPhase('analysing');

      let attempts = 0;
      const analyseTimer = setInterval(async () => {
        attempts += 1;
        let p;
        try { p = await fetch(data.poll_url, { credentials: 'same-origin' }); }
        catch (_) { return; }
        if (!p.ok) {
          clearInterval(analyseTimer);
          setSaveMsg(`We saved your recording but lost track of the analysis job (recording ID ${data.recording_id}). Try again in a minute or email support@contexta.uk.`);
          return;
        }
        let pd;
        try { pd = await p.json(); } catch (_) { pd = {}; }
        if (pd.status === 'done' && pd.check_id) {
          clearInterval(analyseTimer);
          // Stay in React when a handler is provided (Site Check funnel
          // renders the report in-app); otherwise fall back to the server
          // report page (Studio context).
          if (onDone) { onDone(pd.check_id); return; }
          const dest = (data.report_url_template || '/site-check/scan/{check_id}/report')
            .replace('{check_id}', encodeURIComponent(pd.check_id));
          window.location.href = dest;
          return;
        }
        if (pd.status === 'error') {
          clearInterval(analyseTimer);
          setSaveMsg(`Analysis didn't produce a report: ${pd.error || 'unknown error'}. Recording ID ${data.recording_id} is on file.`);
          return;
        }
        if (attempts >= 90) {
          clearInterval(analyseTimer);
          setSaveMsg(`Analysis is taking longer than expected. Recording ID ${data.recording_id} is on file — we'll keep trying.`);
        }
      }, 2000);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('RecorderSession save:', e);
      setError('Network error — please try again.');
      setSaving(false);
    }
  }, [session, onClose, onDone, stopPoll, stopCountdown]);

  // -- Status poll --
  useEffect(() => {
    const poll = async () => {
      try {
        const r = await fetch(session.status_url);
        if (!r.ok) return;
        const s = await r.json();

        const count = s.request_count || 0;
        setReqCount(count);
        // capture_stats is a live extra — absent until the recorder image
        // carrying it is rolled, so the numbers stay at 0 until then.
        if (s.capture_stats) setStats(s.capture_stats);
        if (s.queue_position != null) setQueuePos(s.queue_position);
        // Address bar shows the current page (main-frame) URL. Fall back to
        // the target URL until the worker reports a page_url; latest_url is a
        // "{METHOD} {path}" diagnostic, not a page address, so don't use it here.
        if (s.page_url || s.target_url) setLastUrl(s.page_url || s.target_url);
        if (s.started_at && !startedRef.current) startedRef.current = Date.parse(s.started_at);
        if (s.session_hard_cap_seconds) capRef.current = s.session_hard_cap_seconds;

        if (count !== lastCountRef.current) {
          lastCountRef.current = count;
          lastChangeRef.current = Date.now();
        }
        setStuck(count > 0 && (Date.now() - lastChangeRef.current) >= STUCK_AFTER_MS && s.status === 'recording');

        if (s.display_mode === 'novnc' && s.vnc_iframe_path && !vncPath) {
          const path = s.vnc_iframe_path.replace(/^\/quick-start\/cloud-record\//, '/recorder/cloud-record/');
          setVncPath(path);
          setPhase('recording');
        }

        if (s.display_mode === 'cdp' && s.cdp_client_path && !cdpWsUrl && window.CdpClient) {
          setCdpWsUrl(window.CdpClient.wsUrlFromClientPath(s.cdp_client_path));
          setPhase('recording');
        }

        if (s.latest_status && s.latest_status >= 400 && s.latest_status < 500 &&
            count <= 2 && !authShownRef.current) {
          authShownRef.current = true;
          const label = s.latest_status === 401 ? 'Unauthorised (401)'
            : s.latest_status === 403 ? 'Forbidden (403)' : `HTTP ${s.latest_status}`;
          setAuthOverlay(label);
        }

        if (s.status === 'done' && s.recording_ready) {
          stopPoll();
          await finalise();
        } else if (s.status === 'error') {
          stopPoll();
          setError(s.error || 'Recording errored.');
        }
      } catch (_) {}
    };

    pollRef.current = setInterval(poll, POLL_MS);
    poll();
    return () => { stopPoll(); stopCountdown(); };
  }, [session, vncPath, cdpWsUrl, stopPoll, stopCountdown, finalise]);

  // -- Countdown timer --
  useEffect(() => {
    if (!vncPath && !cdpWsUrl) return;
    const tick = () => {
      if (!startedRef.current) return;
      const left = Math.max(0, capRef.current - Math.floor((Date.now() - startedRef.current) / 1000));
      const m = Math.floor(left / 60);
      const s = (left % 60).toString().padStart(2, '0');
      setCountdown(`${m}:${s}`);
    };
    tick();
    countdownRef.current = setInterval(tick, 1000);
    return stopCountdown;
  }, [vncPath, cdpWsUrl, stopCountdown]);

  // -- CDP screencast: mount the canvas client once we have a WS URL --
  useEffect(() => {
    if (!cdpWsUrl || !canvasRef.current || !window.CdpClient) return undefined;
    const handle = window.CdpClient.mount(canvasRef.current, cdpWsUrl);
    cdpHandleRef.current = handle;
    return () => { try { handle.destroy(); } catch (_) {} cdpHandleRef.current = null; };
  }, [cdpWsUrl]);

  // -- Actions --
  const doFinish = async () => {
    setSaving(true);
    setSaveMsg('Stopping recorder…');
    try { await fetch(session.stop_url, { method: 'POST', credentials: 'same-origin' }); }
    catch (_) {}
  };

  const doCancel = () => {
    stopPoll();
    stopCountdown();
    if (session.stop_url) {
      fetch(session.stop_url, { method: 'POST', credentials: 'same-origin' }).catch(() => {});
    }
    onClose();
  };

  const doPaste = () => {
    if (window.RecorderClient && window.RecorderClient.pasteClipboard && iframeRef.current) {
      window.RecorderClient.pasteClipboard(iframeRef.current);
    }
  };

  const doKeyboard = () => {
    if (window.RecorderClient && window.RecorderClient.showKeyboard && iframeRef.current) {
      window.RecorderClient.showKeyboard(iframeRef.current);
    }
  };

  useRecorderClientScript();
  useCdpClientScript();

  const showVnc = !!vncPath && phase === 'recording';
  const showCdp = !!cdpWsUrl && phase === 'recording';
  const showView = showVnc || showCdp;
  const countdownSecs = countdown ? parseInt(countdown.split(':')[0]) * 60 + parseInt(countdown.split(':')[1]) : 999;
  const addr = (lastUrl || '').replace(/^https?:\/\//, '') || 'Loading…';

  // ── Render ──

  return (
    <div style={S.overlay}>
      <style>{KEYFRAMES}</style>

      {/* ── Recording window ── */}
      {showView && (
        <div style={S.win}>
          <div style={S.hdr}>
            <h1 style={S.h1}>{heading}</h1>
            <div style={S.actions}>
              {!showCdp && (
                <>
                  <button style={S.ghostBtn} onClick={doPaste} title="Paste from clipboard">Paste</button>
                  <button style={S.ghostBtn} onClick={doKeyboard} title="On-screen keyboard">Keyboard</button>
                </>
              )}
              <span style={S.recpill}><span style={S.recDot} />Recording</span>
              <button style={S.donepill} onClick={doFinish}>Finish recording</button>
            </div>
          </div>

          <div style={S.work}>
            <div style={S.browser}>
              <div style={S.chrome}>
                <div style={S.dots}>
                  <span style={{ ...S.dot, background: '#ec6a5e' }} />
                  <span style={{ ...S.dot, background: '#f4bf4f' }} />
                  <span style={{ ...S.dot, background: '#61c554' }} />
                </div>
                <div style={S.addr}><span style={S.lock}>🔒</span>{addr}</div>
              </div>
              {showVnc ? (
                <iframe
                  ref={iframeRef}
                  src={vncPath}
                  style={S.view}
                  sandbox="allow-scripts allow-same-origin allow-forms"
                />
              ) : (
                <canvas
                  ref={canvasRef}
                  width={1280}
                  height={800}
                  style={{ ...S.view, cursor: 'crosshair' }}
                  tabIndex={0}
                />
              )}
            </div>

            <aside style={S.rail}>
              <div style={S.timer}>
                ▶ Live ·{' '}
                <b style={{ color: countdownSecs <= 30 ? '#E0564F' : '#e8ebe9', fontWeight: 500 }}>{countdown}</b> remaining
              </div>
              <div style={S.raillabel}>Captured so far</div>
              <ul style={S.stats}>
                {STATS.map((st, i) => (
                  <li key={st.key} style={i === 0 ? { ...S.statli, ...S.statliFirst } : S.statli}>
                    <div style={S.snum}>{(stats?.[st.key] ?? 0).toLocaleString()}</div>
                    <div style={S.slabel}>{st.label}</div>
                  </li>
                ))}
              </ul>
              <p style={S.hint}>
                {stuck
                  ? <>Nothing new for a bit — open another page so we capture it too. Hit <b style={S.hintB}>Finish recording</b> when you’re done.</>
                  : <>Click around like a normal visitor — open a few pages, try a form or a button. Hit <b style={S.hintB}>Finish recording</b> when you’re done.</>}
              </p>
            </aside>
          </div>
        </div>
      )}

      {/* ── Mobile save bar (recording phase) ── */}
      {showView && (
        <div style={S.mobileSaveBar} className="rec-mobile-savebar">
          <button style={S.mobileBtnGhost} onClick={doCancel}>Cancel</button>
          {!showCdp && (
            <>
              <button style={S.mobileBtnGhost} onClick={doPaste}>Paste</button>
              <button style={S.mobileBtnGhost} onClick={doKeyboard}>Keyboard</button>
            </>
          )}
          <button style={S.mobileFinishBtn} onClick={doFinish}>Finish recording</button>
        </div>
      )}

      {/* ── Warm-up / saving / analysing / error card ── */}
      {!showView && (
        <div style={S.card}>
          {!error && <div style={S.barTrack}><div style={S.bar} /></div>}

          <p style={S.eyebrow}>
            {phase === 'analysing' ? 'Analysing your walk'
              : phase === 'saving' ? 'Saving'
              : error ? 'Couldn’t start'
              : 'Starting your walk'}
          </p>
          <h2 style={S.cardH}>
            {phase === 'analysing' ? 'Turning your walk into a test'
              : phase === 'saving' ? 'Saving your recording'
              : error ? 'Recording didn’t start'
              : 'Setting up your private recorder'}
          </h2>

          {error ? (
            <p style={S.errorText}>{error}</p>
          ) : saving ? (
            <>
              {walkSummary && walkSummary.pages > 0 && (
                <>
                  <p style={S.revealLead}>
                    We walked <b style={S.hintB}>{walkSummary.pages}</b>
                    {walkSummary.pages === 1 ? ' page' : ' pages'} and turned your visit into a replayable test.
                  </p>
                  <div style={S.revealRow}>
                    {REVEAL.map((rv, i) => (
                      <div key={rv.key} style={i === REVEAL.length - 1 ? { ...S.revealCell, borderRight: 'none' } : S.revealCell}>
                        <div style={S.revealNum}>{(walkSummary[rv.key] ?? 0).toLocaleString()}</div>
                        <div style={S.slabel}>{rv.label}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
              <p style={S.cardText}>{saveMsg}</p>
            </>
          ) : (
            <p style={S.cardText}>
              {queuePos != null && queuePos > 1
                ? `You’re number ${queuePos} in the queue — hang on a moment. `
                : ''}
              This takes about <b style={S.hintB}>30 seconds</b> — we’ll drop you straight in when it’s ready.
            </p>
          )}

          <div style={S.cardActions}>
            <button style={S.cancelBtn} onClick={doCancel}>
              {error || saving ? 'Close' : 'Cancel'}
            </button>
          </div>
        </div>
      )}

      {/* ── Auth overlay ── */}
      {authOverlay && (
        <div style={S.authOverlay}>
          <div style={S.authBox}>
            <h3 style={S.authTitle}>This page needs you to sign in</h3>
            <p style={S.authDesc}>
              The site you’re recording returned <b style={S.hintB}>{authOverlay}</b>. Pick what to do next:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button style={S.authBtn} onClick={() => setAuthOverlay(null)}>Go to the site’s login page</button>
              <button style={S.authBtnGhost} onClick={() => setAuthOverlay(null)}>Try a different URL</button>
              <button style={S.authBtnGhost} onClick={doCancel}>Cancel recording</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Live walk stats (keys map to capture_stats) ──
const STATS = [
  { key: 'pages', label: 'Pages walked' },
  { key: 'elements', label: 'Elements seen' },
  { key: 'links', label: 'Links' },
  { key: 'requests', label: 'Requests' },
];

// ── Post-walk reveal (enrichment output, shown on the analysing screen) ──
const REVEAL = [
  { key: 'correlations', label: 'Correlations' },
  { key: 'data_requirements', label: 'Data needed' },
  { key: 'checks', label: 'Checks' },
];

// ── Keyframes ──
const KEYFRAMES = `
@keyframes sc-rec-blink { 0%, 49% { opacity: 1; } 50%, 100% { opacity: 0.18; } }
@keyframes sc-bar { 0% { left: -38%; } 100% { left: 100%; } }
`;

// ── Brand tokens (read off contexta.uk / site-check.css) ──
const C = {
  bg: '#0a0f0d', rule: '#1f2b25', rule2: '#2a3830',
  ink: '#e8ebe9', dim: '#8a9690', gold: '#D5A344', goldh: '#e0b55a',
  teal: '#3f7068', red: '#E0564F',
  mono: "'JetBrains Mono',ui-monospace,SFMono-Regular,Menlo,monospace",
};

const S = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(5,8,7,0.94)', backdropFilter: 'blur(4px)',
    zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 20, boxSizing: 'border-box',
  },

  // Recording window
  win: {
    width: 1040, maxWidth: '100%', maxHeight: '100%', background: C.bg,
    border: `1px solid ${C.rule}`, boxShadow: '0 40px 120px rgba(0,0,0,0.6)',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
  },
  hdr: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20,
    padding: '20px 26px', borderBottom: `1px solid ${C.rule}`,
  },
  h1: {
    fontSize: 30, fontWeight: 500, letterSpacing: '-0.015em', lineHeight: 1, margin: 0,
    color: C.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  actions: { display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 },
  ghostBtn: {
    background: 'transparent', border: `1px solid ${C.rule2}`, color: C.dim,
    padding: '11px 16px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  recpill: {
    display: 'inline-flex', alignItems: 'center', gap: 11, padding: '13px 22px', borderRadius: 999,
    background: 'rgba(224,86,79,0.12)', border: '1px solid rgba(224,86,79,0.4)', color: C.red,
    fontWeight: 600, fontSize: 13.5, letterSpacing: '0.16em', textTransform: 'uppercase',
    cursor: 'default', userSelect: 'none', flexShrink: 0,
  },
  recDot: {
    width: 10, height: 10, borderRadius: '50%', background: C.red, flexShrink: 0,
    boxShadow: '0 0 9px rgba(224,86,79,0.7)', animation: 'sc-rec-blink 1.1s steps(1) infinite',
  },
  donepill: {
    padding: '14px 28px', borderRadius: 999, background: C.gold, color: '#1c1407', border: 'none',
    fontWeight: 700, fontSize: 13.5, letterSpacing: '0.05em', textTransform: 'uppercase',
    cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
  },

  // Workspace
  work: { display: 'flex', gap: 22, padding: '24px 26px', flex: 1, minHeight: 0 },
  browser: {
    flex: 1, minWidth: 0, border: `1px solid ${C.rule2}`, background: '#fff',
    display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
  },
  chrome: {
    display: 'flex', alignItems: 'center', gap: 13, padding: '11px 14px',
    background: '#eef1ef', borderBottom: '1px solid #dde2df',
  },
  dots: { display: 'flex', gap: 8 },
  dot: { width: 11, height: 11, borderRadius: '50%', display: 'block' },
  addr: {
    flex: 1, display: 'flex', alignItems: 'center', gap: 9, background: '#fff',
    border: '1px solid #dde2df', borderRadius: 7, padding: '7px 12px',
    fontFamily: C.mono, fontSize: 12, color: '#5a6660',
    overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
  },
  lock: { color: C.teal, fontSize: 11, flexShrink: 0 },
  view: { width: '100%', height: 440, display: 'block', background: '#fff', border: 0 },

  // Stats rail
  rail: { width: 264, flexShrink: 0, display: 'flex', flexDirection: 'column' },
  timer: {
    fontFamily: C.mono, fontSize: 12, letterSpacing: '0.14em', color: C.dim,
    textTransform: 'uppercase', margin: '2px 0 22px',
  },
  raillabel: {
    fontSize: 12, fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase',
    color: C.dim, margin: '0 0 2px',
  },
  stats: { listStyle: 'none', margin: 0, padding: 0 },
  statli: { borderTop: `1px solid ${C.rule}`, padding: '15px 0' },
  statliFirst: { borderTop: 0, paddingTop: 2 },
  snum: { fontSize: 34, fontWeight: 500, lineHeight: 1, color: C.ink },
  slabel: {
    fontSize: 11, fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase',
    color: C.dim, marginTop: 7,
  },
  hint: { marginTop: 'auto', paddingTop: 20, fontSize: 12.5, lineHeight: 1.55, color: C.dim },
  hintB: { color: C.ink, fontWeight: 600 },

  // Status card (warm-up / saving / analysing / error)
  card: {
    background: C.bg, border: `1px solid ${C.rule2}`, borderLeft: `4px solid ${C.gold}`,
    padding: '32px 36px', maxWidth: 460,
    width: '92%', color: C.ink, boxShadow: '0 30px 80px rgba(0,0,0,0.6)',
  },
  barTrack: { position: 'relative', height: 2, background: C.rule, overflow: 'hidden', margin: '0 0 24px' },
  bar: { position: 'absolute', top: 0, left: '-38%', height: 2, width: '38%', background: C.gold, animation: 'sc-bar 1.25s ease-in-out infinite' },
  eyebrow: {
    fontSize: 12, fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase',
    color: C.dim, margin: '0 0 12px',
  },
  cardH: { fontSize: 24, fontWeight: 500, letterSpacing: '-0.01em', color: C.ink, margin: '0 0 16px', lineHeight: 1.12 },
  cardText: { fontSize: 14, color: C.dim, lineHeight: 1.6, margin: '0 0 22px' },
  errorText: { fontSize: 14, color: '#f0a39d', lineHeight: 1.55, margin: '0 0 22px' },
  revealLead: { fontSize: 14, color: '#c3ccc7', lineHeight: 1.55, margin: '0 0 18px' },
  revealRow: { display: 'flex', borderTop: `1px solid ${C.rule}`, borderBottom: `1px solid ${C.rule}`, margin: '0 0 22px' },
  revealCell: { flex: 1, padding: '15px 6px', textAlign: 'center', borderRight: `1px solid ${C.rule}` },
  revealNum: { fontSize: 28, fontWeight: 500, color: C.ink, lineHeight: 1, marginBottom: 6 },
  cardActions: { display: 'flex', gap: 10, justifyContent: 'flex-end' },
  cancelBtn: {
    background: 'transparent', border: `1px solid ${C.rule2}`, color: C.dim,
    padding: '11px 22px', fontSize: 12.5, fontWeight: 600, letterSpacing: '0.06em',
    textTransform: 'uppercase', cursor: 'pointer', minHeight: 44,
  },

  // Mobile save bar
  mobileSaveBar: {
    display: 'none', position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1100,
    background: 'rgba(10,15,13,0.96)', borderTop: `1px solid ${C.rule}`,
    padding: '12px 16px', gap: 8, alignItems: 'center', justifyContent: 'space-between',
  },
  mobileBtnGhost: {
    minHeight: 44, padding: '10px 14px', background: 'transparent', color: C.ink,
    border: `1px solid ${C.rule2}`, fontSize: 14, fontWeight: 600, cursor: 'pointer', flex: '0 0 auto',
  },
  mobileFinishBtn: {
    minHeight: 44, padding: '10px 18px', background: C.gold, color: '#1c1407', border: 'none',
    fontSize: 14, fontWeight: 700, cursor: 'pointer', flex: '1 1 auto',
  },

  // Auth overlay
  authOverlay: {
    position: 'absolute', inset: 0, background: 'rgba(5,8,7,0.92)', zIndex: 1050,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
  },
  authBox: {
    background: C.bg, border: `1px solid ${C.rule}`, padding: '28px 30px', maxWidth: 440,
    width: '100%', color: C.ink, boxShadow: '0 30px 80px rgba(0,0,0,0.6)',
  },
  authTitle: { fontSize: 19, fontWeight: 500, letterSpacing: '-0.01em', margin: '0 0 8px', color: C.ink },
  authDesc: { fontSize: 13.5, color: C.dim, lineHeight: 1.55, margin: '0 0 18px' },
  authBtn: {
    minHeight: 44, padding: '12px 16px', background: C.gold, color: '#1c1407', border: 'none',
    fontSize: 13, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', cursor: 'pointer',
  },
  authBtnGhost: {
    minHeight: 44, padding: '12px 16px', background: 'transparent', color: C.ink,
    border: `1px solid ${C.rule2}`, fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
  },
};
